import { api } from './client'

/**
 * F-077 / TASK-0331 — movimenti bancari, import dell'estratto conto (CSV), regole di categorizzazione,
 * giroconti, split e riconciliazione con spese e fatture. Nessuna integrazione bancaria (BLK-004), niente Excel,
 * niente IBAN in nessuna forma. Letture: Admin, Amministrazione, Commercialista; scritture: Admin e Amministrazione.
 */

export type Direction = 'Entrata' | 'Uscita'
export type MovementKind = 'Ordinario' | 'TrasferimentoInterno'
export type ReconciliationStatus = 'DaRiconciliare' | 'Parziale' | 'Riconciliato' | 'NonRiconciliabile'
/** `DistintaProvvigioni` (R3-10, flag finanza.dimensioni_estese): la quota va a una distinta di pagamento delle provvigioni. */
export type AllocationTargetType = 'Spesa' | 'Fattura' | 'MovimentoSocio' | 'Altro' | 'DistintaProvvigioni'
export type MatchLevel = 'MatchCerto' | 'MatchProbabile' | 'DaRiconciliare'

export const INCOME_CATEGORIES = ['IncassoOperatore', 'RimborsoSocio', 'Finanziamento', 'Altro'] as const

export const STATUS_LABELS: Record<ReconciliationStatus, string> = {
  DaRiconciliare: 'Da riconciliare',
  Parziale: 'Parziale',
  Riconciliato: 'Riconciliato',
  NonRiconciliabile: 'Non riconciliabile',
}

export const MATCH_LABELS: Record<MatchLevel, string> = {
  MatchCerto: 'Match certo',
  MatchProbabile: 'Match probabile',
  DaRiconciliare: 'Da riconciliare',
}

export interface BankMovement {
  id: number
  accountId: number
  accountName: string | null
  operationDate: string
  valueDate: string | null
  amount: number
  direction: Direction
  bankDescription: string
  counterparty: string | null
  counterpartyIban?: string | null
  reference: string | null
  currency: string
  kind: MovementKind
  transferPairId: number | null
  expenseCategoryId: number | null
  incomeCategory: string | null
  supplierId: number | null
  /** R3-10/R4-03: dimensioni estese (flag finanza.dimensioni_estese). */
  businessUnitId?: number | null
  siteId?: number | null
  teamId?: number | null
  incomeCategoryId?: number | null
  reconciliationStatus: ReconciliationStatus
  allocatedAmount: number
  remainingAmount: number
  source: 'Manuale' | 'ImportCsv'
  importBatchId: number | null
  notes: string | null
  voided: boolean
  voidedAt: string | null
  voidReason: string | null
  createdAt: string
}

export interface MovementAllocation {
  id: number
  movementId: number
  targetType: AllocationTargetType
  targetId: number | null
  amount: number
  categoryId: number | null
  note: string | null
  active: boolean
  removedAt: string | null
  removedReason: string | null
  createdAt: string
}

export interface MovementList {
  total: number
  page: number
  pageSize: number
  summary: { incomes: number; incomesCount: number; outflows: number; outflowsCount: number; note: string }
  items: BankMovement[]
}

export interface MovementFilters {
  accountId?: number | undefined
  from?: string | undefined
  to?: string | undefined
  status?: string | undefined
  direction?: string | undefined
  kind?: string | undefined
  includeVoided?: boolean | undefined
  page?: number | undefined
  pageSize?: number | undefined
  /** F-101: date_desc (default) | date_asc. */
  sort?: string | undefined
}

export interface CreateMovementBody {
  accountId: number
  operationDate: string
  valueDate?: string | undefined
  amount: number
  direction: Direction
  bankDescription: string
  counterparty?: string | undefined
  reference?: string | undefined
  expenseCategoryId?: number | undefined
  incomeCategory?: string | undefined
  supplierId?: number | undefined
  notes?: string | undefined
  force?: boolean | undefined
}

export interface UpdateMovementBody {
  updateClassification?: boolean | undefined
  expenseCategoryId?: number | null | undefined
  incomeCategory?: string | null | undefined
  supplierId?: number | null | undefined
  notes?: string | undefined
  reconcilable?: boolean | undefined
  /** R4-03: > 0 imposta, 0 toglie, assente lascia com'e'. */
  businessUnitId?: number | undefined
  siteId?: number | undefined
  teamId?: number | undefined
  incomeCategoryId?: number | undefined
}

export interface MovementRule {
  id: number
  name: string
  matchField: 'Descrizione' | 'Causale' | 'Controparte' | 'Iban'
  operator: 'contains' | 'startsWith' | 'equals'
  pattern: string
  direction: Direction | null
  accountId: number | null
  expenseCategoryId: number | null
  incomeCategory: string | null
  supplierId: number | null
  priority: number
  isActive: boolean
  timesApplied: number
}

export interface MovementRuleBody {
  name: string
  matchField: string
  operator: string
  pattern: string
  direction?: string | null | undefined
  accountId?: number | null | undefined
  expenseCategoryId?: number | null | undefined
  incomeCategory?: string | null | undefined
  supplierId?: number | null | undefined
  priority?: number | undefined
  isActive?: boolean | undefined
}

export interface CsvProfile {
  dateColumn: string
  descriptionColumn: string
  amountColumn?: string | null | undefined
  debitColumn?: string | null | undefined
  creditColumn?: string | null | undefined
  valueDateColumn?: string | null | undefined
  counterpartyColumn?: string | null | undefined
  referenceColumn?: string | null | undefined
  counterpartyIbanColumn?: string | null | undefined
  hasHeader?: boolean | undefined
  delimiter?: string | null | undefined
}

export interface ImportRowOverride {
  rowNumber: number
  expenseCategoryId?: number | null | undefined
  incomeCategory?: string | null | undefined
  supplierId?: number | null | undefined
}

export interface ImportRequest {
  accountId: number
  csv: string
  fileName?: string | undefined
  profile?: CsvProfile | undefined
  saveProfile?: boolean | undefined
  overrides?: ImportRowOverride[] | undefined
}

export interface ImportPreviewRow {
  rowNumber: number
  operationDate: string | null
  valueDate: string | null
  amount: number
  direction: Direction
  description: string
  counterparty: string | null
  reference: string | null
  error: string | null
  isDuplicate: boolean
  proposed: { ruleId: number; ruleName: string | null; expenseCategoryId: number | null; incomeCategory: string | null; supplierId: number | null } | null
}

export interface ImportPreview {
  accountId: number
  fileName: string | null
  totalRows: number
  validRows: number
  errorRows: number
  duplicateRows: number
  newRows: number
  proposedRows: number
  truncated: boolean
  profile: CsvProfile
  rows: ImportPreviewRow[]
  errors: { rowNumber: number; error: string }[]
}

export interface ImportResult {
  batchId: number | null
  totalRows: number
  newRows: number
  duplicateRows: number
  errorRows: number
  rulesApplied?: number
}

export interface ImportBatch {
  id: number
  accountId: number
  fileName: string
  totalRows: number
  newRows: number
  duplicateRows: number
  errorRows: number
  status: 'Applicato' | 'Annullato'
  createdAt: string
  undoneAt: string | null
  undoReason: string | null
}

export interface Suggestion {
  targetType: AllocationTargetType
  targetId: number
  level: MatchLevel
  reason: string
  score?: number
  criteria?: string[]
  residual: number
  dueDate: string | null
  label: string
}

export interface SuggestionsResponse {
  movementId: number
  level: MatchLevel
  remaining: number
  suggestions: Suggestion[]
}

export interface AllocationItem {
  targetType: AllocationTargetType
  targetId?: number | undefined
  amount: number
  categoryId?: number | undefined
  note?: string | undefined
}

export interface AllocateResponse {
  movement: BankMovement
  allocations: MovementAllocation[]
  statusChanges: { id: number; status: string }[]
}

export interface TransferCandidate {
  outflow: { id: number; accountId: number; accountName: string | null; operationDate: string; amount: number; bankDescription: string }
  inflow: { id: number; accountId: number; accountName: string | null; operationDate: string; amount: number; bankDescription: string }
}

export interface AnomalyItem {
  movement: BankMovement
  ageDays: number
}

export interface Anomalies {
  days: number
  asOf: string
  notReconciledCount: number
  partialCount: number
  notReconciled: AnomalyItem[]
  partial: AnomalyItem[]
}

export interface MonthStatus {
  year: number
  month: number
  allReconciled: boolean
  accounts: { accountId: number; accountName: string; movements: number; pending: number; reconciled: boolean }[]
}

function qs(params: Record<string, string | number | boolean | undefined>): string {
  const q = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') q.set(k, String(v))
  }
  const s = q.toString()
  return s ? `?${s}` : ''
}

export const bankApi = {
  // movimenti
  movements: (f: MovementFilters = {}) =>
    api<MovementList>(`/api/finance/movements${qs({
      accountId: f.accountId, from: f.from, to: f.to, status: f.status, direction: f.direction, kind: f.kind,
      includeVoided: f.includeVoided, page: f.page, pageSize: f.pageSize, sort: f.sort,
    })}`),
  movement: (id: number) =>
    api<{ movement: BankMovement; allocations: MovementAllocation[] }>(`/api/finance/movements/${id}`),
  createMovement: (body: CreateMovementBody) =>
    api<BankMovement>('/api/finance/movements', { method: 'POST', body: JSON.stringify(body) }),
  updateMovement: (id: number, body: UpdateMovementBody) =>
    api<BankMovement>(`/api/finance/movements/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  voidMovement: (id: number, reason: string) =>
    api<BankMovement>(`/api/finance/movements/${id}/void`, { method: 'POST', body: JSON.stringify({ reason }) }),

  // regole
  rules: () => api<{ items: MovementRule[] }>('/api/finance/movement-rules'),
  createRule: (body: MovementRuleBody) =>
    api<MovementRule>('/api/finance/movement-rules', { method: 'POST', body: JSON.stringify(body) }),
  updateRule: (id: number, body: MovementRuleBody) =>
    api<MovementRule>(`/api/finance/movement-rules/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteRule: (id: number) => api<void>(`/api/finance/movement-rules/${id}`, { method: 'DELETE' }),
  ruleFromMovement: (movementId: number, pattern?: string) =>
    api<MovementRule>(`/api/finance/movement-rules/from-movement/${movementId}`, {
      method: 'POST',
      body: JSON.stringify(pattern ? { pattern } : {}),
    }),

  // import
  profile: (accountId: number) =>
    api<{ accountId: number; profile: CsvProfile | null }>(`/api/finance/bank-imports/profiles/${accountId}`),
  saveProfile: (accountId: number, profile: CsvProfile) =>
    api<{ accountId: number; profile: CsvProfile }>(`/api/finance/bank-imports/profiles/${accountId}`, {
      method: 'PUT',
      body: JSON.stringify(profile),
    }),
  imports: (accountId?: number) =>
    api<{ items: ImportBatch[] }>(`/api/finance/bank-imports${qs({ accountId })}`),
  previewImport: (body: ImportRequest) =>
    api<ImportPreview>('/api/finance/bank-imports/preview', { method: 'POST', body: JSON.stringify(body) }),
  applyImport: (body: ImportRequest) =>
    api<ImportResult>('/api/finance/bank-imports/apply', { method: 'POST', body: JSON.stringify(body) }),
  undoImport: (batchId: number, reason: string) =>
    api<{ batchId: number; status: string; removedMovements: number }>(`/api/finance/bank-imports/${batchId}/undo`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  // riconciliazione
  suggestions: (movementId: number) =>
    api<SuggestionsResponse>(`/api/finance/reconciliation/suggestions?movementId=${movementId}`),
  allocate: (movementId: number, items: AllocationItem[], closedPeriodReason?: string) =>
    api<AllocateResponse>(`/api/finance/reconciliation/${movementId}/allocations`, {
      method: 'POST',
      body: JSON.stringify({ items, ...(closedPeriodReason ? { closedPeriodReason } : {}) }),
    }),
  deallocate: (allocationId: number, reason: string) =>
    api<{ movement: BankMovement; targetStatusNotReverted: boolean; note: string | null }>(
      `/api/finance/reconciliation/allocations/${allocationId}?reason=${encodeURIComponent(reason)}`,
      { method: 'DELETE' },
    ),
  transferCandidates: () =>
    api<{ items: TransferCandidate[] }>('/api/finance/reconciliation/transfer-candidates'),
  linkTransfer: (a: number, b: number) =>
    api<{ outflowId: number; inflowId: number }>(`/api/finance/reconciliation/transfers/${a}/${b}`, { method: 'POST' }),
  unlinkTransfer: (id: number) =>
    api<{ unlinked: number[] }>(`/api/finance/reconciliation/transfers/${id}`, { method: 'DELETE' }),
  anomalies: (days = 7) => api<Anomalies>(`/api/finance/reconciliation/anomalies?days=${days}`),
  monthStatus: (year: number, month: number) =>
    api<MonthStatus>(`/api/finance/reconciliation/status?year=${year}&month=${month}`),
}
